import { EPlatform } from '@/utils/enums/platform-enum';

/** 服务端 Script 与客户端兜底共享的 Sensors 节点 ID。 */
export const SENSORS_SDK_SCRIPT_ID = 'zeelool-sensors-sdk';

/** 当前生产使用的 Sensors JavaScript SDK 固定版本地址。 */
export const SENSORS_SDK_URL =
  'https://common.xmslol.com/libs/sensorsdata@1.26.18/web/sensorsdata.js';

/** 单页面生命周期内复用的 Sensors 兜底加载 Promise。 */
let sensorsSdkPromise: Promise<void> | null = null;

/** 单页面生命周期内复用的 Snowplow browser tracker Promise。 */
let browserTrackerPromise: Promise<typeof import('@snowplow/browser-tracker')> | null = null;

/** 单页面生命周期内复用的 Snowplow webview tracker Promise。 */
let webviewTrackerPromise: Promise<typeof import('@snowplow/webview-tracker')> | null = null;

/** 加载 Sensors SDK；SSR Script 已执行时直接完成，否则复用已有节点或创建唯一兜底节点。 */
export function loadSensorsSdk(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.sensorsDataAnalytic201505) return Promise.resolve();
  if (sensorsSdkPromise) return sensorsSdkPromise;

  // Next 的 beforeInteractive 脚本在 hydration 前已完成 load/error；此时无 SDK 全局即表示旧 SSR 节点失效。
  const existingScript = document.getElementById(SENSORS_SDK_SCRIPT_ID);
  existingScript?.remove();
  const script = document.createElement('script');

  const loadPromise = new Promise<void>((resolve, reject) => {
    script.onload = () => {
      if (window.sensorsDataAnalytic201505) {
        resolve();
        return;
      }
      reject(new Error('Sensors SDK global is unavailable after script load'));
    };
    script.onerror = () => reject(new Error('Failed to load Sensors Analytics script'));

    script.id = SENSORS_SDK_SCRIPT_ID;
    script.async = true;
    script.fetchPriority = 'high';
    script.src = SENSORS_SDK_URL;
    document.head.appendChild(script);
  });

  // 动态兜底失败后移除失效节点并清空 Promise，允许后续显式调用重新下载。
  sensorsSdkPromise = loadPromise.catch((error: unknown) => {
    script.remove();
    sensorsSdkPromise = null;
    throw error;
  });
  return sensorsSdkPromise;
}

/** 返回缓存式 Snowplow browser tracker 模块 Promise。 */
export function loadSnowplowBrowserTracker(): Promise<typeof import('@snowplow/browser-tracker')> {
  browserTrackerPromise ??= import('@snowplow/browser-tracker');
  return browserTrackerPromise;
}

/** 返回缓存式 Snowplow webview tracker 模块 Promise。 */
export function loadSnowplowWebviewTracker(): Promise<typeof import('@snowplow/webview-tracker')> {
  webviewTrackerPromise ??= import('@snowplow/webview-tracker');
  return webviewTrackerPromise;
}

/** 按运行平台提前启动正确的 Snowplow Tracker 模块下载。 */
export function preloadSnowplowTracker(deviceType: EPlatform): Promise<unknown> {
  return [EPlatform.IOS, EPlatform.ANDROID].includes(deviceType)
    ? loadSnowplowWebviewTracker()
    : loadSnowplowBrowserTracker();
}
